import { useEffect, useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams } from "@tanstack/react-router"

import type { HelpCenterArticle } from "@productlane/db"
import { getPortalUrl } from "@productlane/lib/src/domain"
import { flattenSubpageTree } from "@productlane/lib/src/help-center/subpage-tree"
import { useBreakpoints } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import { MetaInfo } from "@/components/meta-info"
import { PortalInstanceSwitcher } from "@/components/portal-instance-switcher"
import { isQuerySettled } from "@/lib/query-settled"
import { useZeroWorkspace } from "@/lib/zero"
import { useActivePortalInstance } from "@/stores/active-portal-instance-store"
import { useDocNavStore } from "@/stores/docs/docs-nav-store"
import { portalTabKey, useDocsTabStore } from "@/stores/docs/docs-tab-store"
import { PublishPortalPageButton } from "../portal/publish-portal-page-button"
import { ApiReferencePage } from "./api-reference-page"
import { ApiReferencePreview } from "./api-reference-preview"
import { ApiReferenceSettingsDialog } from "./api-reference-settings-dialog"
import { isLinkArticle } from "./article-kind"
import {
	findArticlePortalInstanceId,
	useFollowArticlePortal,
} from "./article-portal-instance"
import { resolveDocsArticleId } from "./docs-breadcrumb-trail"
import { DocsHeaderTabsProvider } from "./docs-header-tabs"
import { DocsTabBar } from "./docs-tab-bar"
import { HelpCenterArticleView } from "./help-center-article"
import { HelpCenterArticleProvider } from "./help-center-article-context"
import { HelpCenterProvider } from "./help-center-context"
import { HelpCenterHomeView } from "./help-center-home"
import { HelpCenterNav } from "./help-center-nav"
import { HelpCenterArticleReviewView } from "./help-center-review-view"
import { HelpCenterSettings } from "./help-center-settings"
import { folderKeyForArticle } from "./hidden-tier-folders"
import { LinkArticleView } from "./link-article-view"
import { NewApiReferenceDialog } from "./new-api-reference-dialog"
import { NewDocsTabDialog } from "./new-docs-tab-dialog"
import { pickReviewArticle } from "./pick-review-article"
import {
	HelpCenterSuggestionsProvider,
	useArticleSuggestions,
} from "./use-article-suggestions"
import {
	groupTabId,
	homeReplacementArticleId,
	isRestoredFromOtherTab,
	portalSwitchRoute,
	useDocsTabs,
} from "./use-docs-tabs"
import { usePrefetchApiReferences } from "./use-prefetch-api-references"

const helpCenterQueries = () => ({
	articleGroups: queries.helpCenterArticlesWithGroups(),
})

export const helpCenterPagePreloadQueries = () => [
	helpCenterQueries().articleGroups,
]

export function HelpCenterScreen() {
	// Mount the provider once at the route root so the screen body and the
	// nav's `SuggestionsSection` share a single useQuery + useMemo bundle.
	return (
		<HelpCenterSuggestionsProvider>
			<HelpCenterScreenBody />
		</HelpCenterSuggestionsProvider>
	)
}

function HelpCenterScreenBody() {
	const workspace = useZeroWorkspace()
	const { lastOpenedArticleId, openGroup, isNavOpen, setIsNavOpen } =
		useDocNavStore()
	const navigate = useNavigate()
	const { isMobile } = useBreakpoints()
	const params = useParams({ strict: false })
	const reviewArticleId = params.articleId
	const reviewProposalId = params.proposalId
	const apiTabId = params.tabId ?? null
	const apiGroupSlug = params.groupSlug ?? null
	const apiPageSlug = params.slug ?? null
	const isReviewMode = Boolean(reviewArticleId || reviewProposalId)
	const paramsId: HelpCenterArticle["id"] = params.id ?? reviewArticleId ?? ""
	const resolvedId = resolveDocsArticleId({
		paramsId,
		isReviewMode,
		lastOpenedArticleId,
		isMobile,
	})
	// The API reference page owns the right pane, so no article opens behind it.
	const id = apiTabId ? "" : resolvedId
	const [articleGroupsRaw, articleGroupsResult] = useQuery(
		helpCenterQueries().articleGroups,
	)

	const portalInstances = workspace?.portalInstances ?? []
	// Multi-portal is "active" as soon as there's 1+ portal beyond the implicit
	// Root. Root itself is `portalInstanceId === null` and is always there.
	const multiPortalActive = portalInstances.length >= 1
	const [activePortalInstanceId, setActivePortalInstanceId] =
		useActivePortalInstance(portalInstances)
	const activePortalSlug =
		portalInstances.find((instance) => instance.id === activePortalInstanceId)
			?.slug ?? null

	// Direct-linking to a suggestion review URL must always land the user on
	// the main (root) portal — suggestions are tied to it for now, and the
	// review view (and the surrounding nav) only render correctly when the
	// active portal matches.
	useEffect(() => {
		if (isReviewMode && activePortalInstanceId !== null) {
			setActivePortalInstanceId(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isReviewMode])

	// A docs deep link can target an article outside the selected portal, which
	// the portal filter would hide; follow the article's own portal instead.
	const urlArticlePortalInstanceId = useMemo(
		() =>
			isReviewMode
				? undefined
				: findArticlePortalInstanceId(articleGroupsRaw, paramsId),
		[isReviewMode, articleGroupsRaw, paramsId],
	)
	useFollowArticlePortal({
		enabled: multiPortalActive,
		articlePortalInstanceId: urlArticlePortalInstanceId,
		activePortalInstanceId,
		setActivePortalInstanceId,
	})

	// Filter the loaded tree by the active portal. Both sides are nullable —
	// `null === null` matches Root, otherwise the id must match exactly. Groups
	// themselves are scoped, so we drop any whose `portalInstanceId` doesn't
	// match the active portal; their articles are then filtered the same way.
	const articleGroups = useMemo(() => {
		const scoped = multiPortalActive
			? articleGroupsRaw
					.filter((group) => group.portalInstanceId === activePortalInstanceId)
					.map((group) => ({
						...group,
						helpCenterArticles: group.helpCenterArticles.filter(
							(article) => article.portalInstanceId === activePortalInstanceId,
						),
					}))
			: articleGroupsRaw
		return scoped.map((group) => ({
			...group,
			helpCenterArticles: group.helpCenterArticles.map((article) => ({
				...article,
				subpages: flattenSubpageTree(article.subpages),
			})),
		}))
	}, [multiPortalActive, articleGroupsRaw, activePortalInstanceId])
	const rootArticles = articleGroups.flatMap(
		(group) => group.helpCenterArticles,
	)
	const allArticles = rootArticles.flatMap((rootArticle) => [
		rootArticle,
		...(rootArticle.subpages ?? []),
	])
	// /edit-docs restores the last article only when it belongs to the active tab,
	// so switching tabs never reopens a page from the tab just left.
	const {
		tabs: docsTabs,
		allTabs: allDocsTabs,
		activeTabId,
		setActiveTabId,
	} = useDocsTabs()
	usePrefetchApiReferences(docsTabs)
	// A default tab that starts with a top-level page opens on it, the way the
	// docs site does. A phone shows the nav tree there instead.
	const landingId =
		!id && !isReviewMode && !apiTabId && !isMobile && activeTabId === null
			? homeReplacementArticleId(articleGroups, docsTabs)
			: null
	const openId = id || landingId || ""
	const candidate = allArticles.find((a) => a.id === openId)
	const candidateGroup = articleGroups.find(
		(g) => g.id === candidate?.helpCenterGroupId,
	)
	const hideRestored = isRestoredFromOtherTab({
		paramsId,
		group: candidateGroup,
		tabs: docsTabs,
		activeTabId,
	})
	const article = hideRestored ? undefined : candidate
	// An article detached from every group is absent from the group-rooted tree
	// query above, so a review URL pointing at one resolves it by id.
	const [detachedReviewArticle] = useQuery(
		queries.helpCenterArticleById(reviewArticleId ?? ""),
		{ enabled: Boolean(reviewArticleId) },
	)
	const group = articleGroups.find((g) => g.id === article?.helpCenterGroupId)

	const articleId = article?.id

	const suggestions = useArticleSuggestions()
	const reviewRow = reviewArticleId
		? suggestions.rowByArticleId.get(reviewArticleId)
		: null
	const reviewArticle = pickReviewArticle(
		article,
		detachedReviewArticle,
		reviewArticleId,
	)
	const createReviewProposal = reviewProposalId
		? suggestions.proposalById.get(reviewProposalId)
		: null

	// Opening an article from another tab, by link or search, switches the nav to that tab.
	const articleTabId =
		paramsId && group ? groupTabId(group, docsTabs) : undefined
	useEffect(() => {
		if (articleTabId !== undefined && articleTabId !== activeTabId) {
			setActiveTabId(articleTabId)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [articleId, articleTabId])

	useEffect(() => {
		// Timeout for smoother animation
		setTimeout(() => {
			if (article?.helpCenterGroupId) {
				openGroup(folderKeyForArticle(article, article.helpCenterGroupId))
			}
		}, 200)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const onItemSelected = () => {
		if (isNavOpen) {
			setIsNavOpen(false)
		}
	}

	// Switching portals while the URL targets a specific article or proposal
	// would leave the user on a URL whose resource doesn't exist on the new
	// portal. Bounce them to `/edit-docs` (the portal-aware docs home) so the
	// view matches the freshly-selected portal.
	const isResourceUrl = Boolean(
		paramsId || reviewArticleId || reviewProposalId || apiTabId,
	)
	const onPortalChange = (id: string | null) => {
		setActivePortalInstanceId(id)
		const route = portalSwitchRoute({
			portalInstanceId: id,
			allTabs: allDocsTabs,
			storedTabId:
				useDocsTabStore.getState().activeByPortal[portalTabKey(id)] ?? null,
			onPortalPage: isResourceUrl,
		})
		if (route) void navigate({ href: route })
	}

	return (
		<HelpCenterProvider
			onItemSelected={onItemSelected}
			articlesSettled={isQuerySettled(articleGroupsResult)}
		>
			<HelpCenterArticleProvider>
				<DocsHeaderTabsProvider
					value={<DocsTabBar articleGroups={articleGroups} />}
				>
					<MetaInfo title="Docs" />
					<NewApiReferenceDialog />
					<NewDocsTabDialog />
					<ApiReferenceSettingsDialog />
					<div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden border-r bg-white dark:bg-gray-900">
						<div className="flex min-h-0 w-full flex-1 overflow-hidden">
							<HelpCenterNav
								// Re-mount the nav subtree when the active portal flips so its
								// internal mirrored state (groupsWithArticles) is rebuilt with
								// the filtered list rather than reusing stale entries.
								key={activePortalInstanceId ?? "single-portal"}
								articleGroups={articleGroups ?? []}
								selectedArticle={article}
								isDetailOpen={
									Boolean(article) || isReviewMode || Boolean(apiTabId)
								}
								isHomeActive={!apiTabId && !isReviewMode && !article}
								portalSwitcher={
									multiPortalActive ? (
										<PortalInstanceSwitcher
											activeId={activePortalInstanceId}
											onChange={onPortalChange}
										/>
									) : undefined
								}
								mobileActions={
									workspace && (
										<PublishPortalPageButton
											content={<HelpCenterSettings />}
											name="docs"
											url={getPortalUrl(workspace, "/docs", activePortalSlug)}
											isPublished={workspace.helpCenterPublic}
										/>
									)
								}
							/>
							{apiTabId ? (
								<div className="relative flex-shrink flex-grow overflow-hidden bg-white dark:bg-gray-900">
									{apiGroupSlug && apiPageSlug ? (
										<ApiReferencePreview
											key={`${apiTabId}/${apiGroupSlug}/${apiPageSlug}`}
											tabId={apiTabId}
											groupSlug={apiGroupSlug}
											slug={apiPageSlug}
										/>
									) : (
										<ApiReferencePage key={apiTabId} tabId={apiTabId} />
									)}
								</div>
							) : isReviewMode && createReviewProposal ? (
								<div className="relative flex-shrink flex-grow overflow-hidden bg-white dark:bg-gray-900">
									<HelpCenterArticleReviewView
										mode="create"
										proposal={createReviewProposal}
										key={createReviewProposal.id}
									/>
								</div>
							) : isReviewMode && reviewRow && reviewArticle ? (
								<div className="relative flex-shrink flex-grow overflow-hidden bg-white dark:bg-gray-900">
									<HelpCenterArticleReviewView
										mode="existing"
										row={reviewRow}
										article={reviewArticle}
										group={group}
										key={`review-${reviewArticle.id}`}
									/>
								</div>
							) : !openId || !article ? (
								// A phone gets the nav's tree instead of this home.
								<div className="relative flex-shrink flex-grow overflow-hidden bg-white max-sm:hidden dark:bg-gray-900">
									<HelpCenterHomeView articleGroups={articleGroups} />
								</div>
							) : (
								<div className="relative flex-shrink flex-grow overflow-hidden bg-white dark:bg-gray-900">
									{!!article &&
										!!group &&
										(isLinkArticle(article) ? (
											<LinkArticleView article={article} key={article.id} />
										) : (
											<HelpCenterArticleView
												group={group}
												article={article}
												key={article.id}
											/>
										))}
								</div>
							)}
						</div>
					</div>
				</DocsHeaderTabsProvider>
			</HelpCenterArticleProvider>
		</HelpCenterProvider>
	)
}
